import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Building2, RefreshCw, CheckCircle, XCircle, Search, Zap,
  Globe, FileText, MapPin, Phone, Key, Plus, Trash2
} from 'lucide-react';

const CompanyDataPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [result, setResult] = useState(null);
  const [lookupCui, setLookupCui] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Multi-key state
  const [keysData, setKeysData] = useState({ keys: [], total_keys: 0, configured: false });
  const [newKey, setNewKey] = useState('');
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [addingKey, setAddingKey] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [suppRes, keysRes] = await Promise.all([
        api.get('/suppliers'),
        api.get('/company-data/keys')
      ]);
      setSuppliers(suppRes.data);
      setKeysData(keysRes.data);
    } catch { toast.error('Eroare la incarcarea datelor'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddKey = async () => {
    if (!newKey.trim()) return;
    setAddingKey(true);
    try {
      await api.post('/company-data/keys', { api_key: newKey.trim(), label: newKeyLabel.trim() });
      toast.success('Cheie API adaugata!');
      setNewKey('');
      setNewKeyLabel('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la adaugare');
    } finally { setAddingKey(false); }
  };

  const handleRemoveKey = async (keyId) => {
    if (!window.confirm('Stergeti aceasta cheie API?')) return;
    try {
      await api.delete(`/company-data/keys/${keyId}`);
      toast.success('Cheie API stearsa');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
  };

  const handleBulkUpdate = async () => {
    setUpdating(true);
    setResult(null);
    try {
      const res = await api.post('/company-data/update-all-suppliers');
      setResult(res.data);
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la actualizare');
    } finally { setUpdating(false); }
  };

  const handleSingleUpdate = async (supplierId) => {
    setUpdatingId(supplierId);
    try {
      const res = await api.post(`/company-data/update-supplier/${supplierId}`);
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la actualizare');
    } finally { setUpdatingId(null); }
  };

  const handleLookup = async () => {
    if (!lookupCui.trim()) return;
    setLookupLoading(true);
    setLookupResult(null);
    try {
      const res = await api.get(`/company-data/lookup/${lookupCui.trim()}`);
      setLookupResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'CUI negasit');
    } finally { setLookupLoading(false); }
  };

  const suppliersWithCui = suppliers.filter(s => s.cif || s.cui);
  const suppliersWithoutCui = suppliers.filter(s => !s.cif && !s.cui);

  return (
    <div className="space-y-6" data-testid="company-data-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Date Firme - OpenAPI.ro</h1>
          <p className="text-slate-500 text-sm mt-1">Actualizare automata date furnizori din baza de date ANAF</p>
        </div>
        <Button
          onClick={handleBulkUpdate}
          disabled={updating || suppliersWithCui.length === 0 || !keysData.configured}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="bulk-update-btn"
        >
          {updating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
          Actualizare Furnizori ({suppliersWithCui.length})
        </Button>
      </div>

      {/* Bulk Update Result */}
      {result && (
        <Card className={result.errors?.length > 0 ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'} data-testid="bulk-result-card">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-slate-900">{result.message}</p>
                {result.details?.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {result.details.map((d, i) => (
                      <p key={i} className="text-sm text-green-700">
                        {d.supplier} (CUI: {d.cui}) - {d.fields.length} campuri
                      </p>
                    ))}
                  </div>
                )}
                {result.errors?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {result.errors.map((e, i) => (
                      <p key={i} className="text-sm text-red-600">
                        {e.supplier} (CUI: {e.cui}): {e.error}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Suppliers with CUI */}
        <div className="lg:col-span-2 space-y-4">
          <Card data-testid="suppliers-with-cui-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-600" />
                Furnizori cu CUI/CIF ({suppliersWithCui.length})
              </CardTitle>
              <CardDescription>Acesti furnizori pot fi actualizati automat din ANAF</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 text-slate-400 animate-spin" /></div>
              ) : suppliersWithCui.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">Niciun furnizor cu CUI/CIF completat</p>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {suppliersWithCui.map(s => (
                    <div key={s.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm truncate">{s.name}</p>
                          <Badge variant="secondary" className="text-xs font-mono">{s.cif || s.cui}</Badge>
                          {s.vat_payer && <Badge className="text-[10px] bg-green-100 text-green-700 hover:bg-green-100">TVA</Badge>}
                          {s.anaf_status && <Badge variant="outline" className="text-[10px]">{s.anaf_status}</Badge>}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                          {(s.locality || s.county) && (
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{[s.locality, s.county].filter(Boolean).join(', ')}</span>
                          )}
                          {s.registry_number && <span>Reg: {s.registry_number}</span>}
                          {s.caen_code && <span>CAEN: {s.caen_code}</span>}
                          {s.phone_fixed && (
                            <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{s.phone_fixed}</span>
                          )}
                          {s.openapi_updated_at && (
                            <span className="text-green-600">Actualizat: {new Date(s.openapi_updated_at).toLocaleDateString('ro-RO')}</span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSingleUpdate(s.id)}
                        disabled={updatingId === s.id}
                        data-testid={`update-supplier-${s.id}`}
                      >
                        {updatingId === s.id ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <><Globe className="w-3.5 h-3.5 mr-1" />ANAF</>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {suppliersWithoutCui.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                  <XCircle className="w-4 h-4" />
                  Furnizori fara CUI ({suppliersWithoutCui.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {suppliersWithoutCui.map(s => (
                    <div key={s.id} className="flex items-center justify-between p-2 text-sm text-slate-600">
                      <span>{s.name}</span>
                      <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Lipseste CUI</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Multi API Keys */}
          <Card data-testid="api-keys-card" className={keysData.configured ? 'border-green-200' : 'border-red-200'}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Key className="w-4 h-4 text-indigo-600" />
                Chei API OpenAPI.ro
              </CardTitle>
              <CardDescription>
                Adaugati mai multe chei pentru a depasi limita de 100 cereri/zi.
                Cheile se rotesc automat.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Existing keys */}
              {keysData.keys.length > 0 ? (
                <div className="space-y-2">
                  {keysData.keys.map((k, idx) => (
                    <div key={k.id} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 border">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{k.label || `Cheie #${idx + 1}`}</span>
                          <Badge variant="outline" className="text-[10px] font-mono">{k.api_key_masked}</Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                          <span>Utilizare azi: <strong className={k.usage_today >= 90 ? 'text-red-600' : 'text-green-600'}>{k.usage_today}/100</strong></span>
                          {k.last_used && <span>Ultima: {new Date(k.last_used).toLocaleDateString('ro-RO')}</span>}
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleRemoveKey(k.id)} data-testid={`remove-key-${k.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-slate-400 text-sm">
                  {keysData.has_env_key ? (
                    <p className="text-green-600">Cheie din variabila ENV activa</p>
                  ) : (
                    <p>Nicio cheie configurata</p>
                  )}
                </div>
              )}

              {/* Total capacity */}
              {keysData.keys.length > 0 && (
                <div className="text-center text-xs text-slate-500 py-1 border-t">
                  Capacitate totala: <strong>{keysData.keys.length * 100} cereri/zi</strong> ({keysData.keys.length} chei x 100)
                </div>
              )}

              {/* Add new key */}
              <div className="space-y-2 pt-2 border-t">
                <Label className="text-sm">Adauga cheie noua</Label>
                <Input
                  value={newKeyLabel}
                  onChange={e => setNewKeyLabel(e.target.value)}
                  placeholder="Eticheta (ex: Cont personal, Cont firma)"
                  className="h-9 text-sm"
                  data-testid="new-key-label-input"
                />
                <div className="flex gap-2">
                  <Input
                    type="password"
                    value={newKey}
                    onChange={e => setNewKey(e.target.value)}
                    placeholder="API Key de la openapi.ro"
                    className="h-9 text-sm"
                    data-testid="new-api-key-input"
                  />
                  <Button onClick={handleAddKey} disabled={addingKey || !newKey.trim()} size="sm" data-testid="add-api-key-btn">
                    {addingKey ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-slate-400">
                  Obtineti chei de la <a href="https://openapi.ro" target="_blank" rel="noreferrer" className="text-blue-600 underline">openapi.ro</a> (100 cereri/zi gratuit per cheie)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* CUI Lookup */}
          <Card data-testid="cui-lookup-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Search className="w-4 h-4 text-green-600" />
                Verificare CUI/CIF
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input
                  value={lookupCui}
                  onChange={e => setLookupCui(e.target.value)}
                  placeholder="ex: 12345678"
                  onKeyDown={e => e.key === 'Enter' && handleLookup()}
                  data-testid="cui-lookup-input"
                />
                <Button onClick={handleLookup} disabled={lookupLoading || !lookupCui.trim()} data-testid="cui-lookup-btn">
                  {lookupLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>

              {lookupResult && lookupResult.data && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-2" data-testid="cui-lookup-result">
                  <p className="font-semibold text-green-900">{lookupResult.data.name}</p>
                  <div className="space-y-1 text-xs text-green-800">
                    {lookupResult.data.cif && <p><strong>CIF:</strong> {lookupResult.data.cif}</p>}
                    {lookupResult.data.registry_number && <p><strong>Reg. Com.:</strong> {lookupResult.data.registry_number}</p>}
                    {lookupResult.data.address && <p><strong>Adresa:</strong> {lookupResult.data.address}</p>}
                    {lookupResult.data.locality && <p><strong>Localitate:</strong> {lookupResult.data.locality}</p>}
                    {lookupResult.data.county && <p><strong>Judet:</strong> {lookupResult.data.county}</p>}
                    {lookupResult.data.phone && <p><strong>Telefon:</strong> {lookupResult.data.phone}</p>}
                    {lookupResult.data.caen_code && <p><strong>CAEN:</strong> {lookupResult.data.caen_code} - {lookupResult.data.caen_description}</p>}
                    {lookupResult.data.vat_payer !== undefined && (
                      <p><strong>Platitor TVA:</strong> {lookupResult.data.vat_payer ? 'Da' : 'Nu'}</p>
                    )}
                    {lookupResult.data.status && <p><strong>Status:</strong> {lookupResult.data.status}</p>}
                    {lookupResult.data.registration_date && <p><strong>Data inregistrare:</strong> {lookupResult.data.registration_date}</p>}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-2">
                <FileText className="w-4 h-4 text-blue-600 mt-0.5" />
                <div className="text-xs text-blue-800">
                  <p className="font-medium">Despre rotirea cheilor</p>
                  <p className="mt-1">Cand o cheie atinge limita de 100 cereri/zi, sistemul trece automat la urmatoarea cheie. Adaugati mai multe chei pentru actualizari in masa.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CompanyDataPage;
